import { useEffect, useState, useCallback, useRef } from "react"

interface ExitIntentOptions {
   delayInSeconds?: number
   onExitIntent?: () => void
   sensitivity?: number
}

export const useExitIntent = (options: ExitIntentOptions = {}) => {
   const {
      delayInSeconds = 10, // Delay before activating exit intent detection
      onExitIntent, // Callback when exit intent is triggered
      sensitivity = 50, // Distance from the top of the viewport to trigger exit intent
   } = options

   const [isTriggered, setIsTriggered] = useState(false)
   const [isActive, setIsActive] = useState(false)
   const previousY = useRef<number | null>(null)

   const handleMouseMove = useCallback(
      (e: MouseEvent) => {
         const currentY = e.clientY

         // Check if we have a previous position and if mouse is moving upward
         if (previousY.current !== null && currentY < previousY.current) {
            // Detect mouse moving to the top of the viewport (exit intent) while moving up
            if (currentY <= sensitivity) {
               if (!isTriggered && isActive) {
                  setIsTriggered(true)
                  onExitIntent?.()
               }
            }
         }

         // Update previous position
         previousY.current = currentY
      },
      [isTriggered, isActive, onExitIntent, sensitivity],
   )

   const handleMouseLeave = useCallback(
      (e: MouseEvent) => {
         // Only trigger if mouse leaves from the top of the document
         if (e.clientY <= 0) {
            if (!isTriggered && isActive) {
               setIsTriggered(true)
               onExitIntent?.()
            }
         }
      },
      [isTriggered, isActive, onExitIntent],
   )

   const resetTrigger = useCallback(() => {
      setIsTriggered(false)
      previousY.current = null // Reset previous position when resetting trigger
   }, [])

   useEffect(() => {
      // Activate the hook after the specified delay
      const timeoutId = setTimeout(() => {
         setIsActive(true)
      }, delayInSeconds * 1000)

      return () => {
         clearTimeout(timeoutId)
      }
   }, [delayInSeconds])

   useEffect(() => {
      if (!isActive) return

      // Add event listeners when active
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseleave", handleMouseLeave)

      return () => {
         document.removeEventListener("mousemove", handleMouseMove)
         document.removeEventListener("mouseleave", handleMouseLeave)
      }
   }, [isActive, handleMouseMove, handleMouseLeave])

   return {
      isTriggered,
      resetTrigger,
      isActive,
   }
}
